import {
  As,
  Badge,
  Button,
  ButtonProps,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { DocumentTextIcon } from '@heroicons/react/outline';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { DocumentStatus } from '@blockpulse3/graphql/hooks';
import { useBadge } from '@blockpulse3/ui/ui-hooks';

import { styles } from './styles';

type Props = {
  /* ** Is callback disabled ** */
  isDisabled?: boolean;
  /* ** Is step badge loading ** */
  isBadgeLoading?: boolean;
  /* ** Custom icon ** */
  icon?: As;
  /* ** Title ** */
  title: string;
  /* ** Status of the document ** */
  status: string;
  /* ** Default size ** */
  size?: 'md' | 'sm';
  /* ** Callback on click ** */
  onClick?: () => void;
} & Omit<ButtonProps, 'size'>;

/**
 * SignatureDocument.
 * Custom component to render document to be signed with its status.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SignatureDocument({
  isDisabled = false,
  isBadgeLoading = false,
  icon = DocumentTextIcon,
  title,
  status,
  size = 'md',
  onClick = noop,
  ...props
}: Props): JSX.Element {
  const i18nSignatureDocument = useTranslations('SignatureDocument');

  /* ** Render badge label and color for different status ** */
  const { badge } = useBadge(
    status,
    [
      { value: DocumentStatus.SIGNED, label: i18nSignatureDocument('Signed'), color: 'green' },
      { value: DocumentStatus.CANCELLED, label: i18nSignatureDocument('Cancelled'), color: 'red' },
      {
        value: DocumentStatus.SIGNATURES_PENDING,
        label: i18nSignatureDocument('SignaturesPending'),
        color: 'yellow',
      },
    ],
    {
      color: 'gray',
      label: i18nSignatureDocument('RequiredAction'),
    },
  );

  const handleDocumentClick = (): void => {
    if (!isDisabled) {
      onClick();
    }
  };

  /* ** Set cursor if disabled ** */
  const cursor = isDisabled ? 'not-allowed' : 'pointer';

  return (
    <Button
      _hover={{ bg: 'gray.50' }}
      alignItems={{ base: 'flex-start', md: 'center' }}
      aria-disabled="false"
      cursor={cursor}
      direction={{ base: 'column', md: 'row' }}
      flexWrap="wrap"
      justifyContent="space-between"
      p="2"
      rounded="sm"
      variant="ghost"
      onClick={handleDocumentClick}
      {...props}
    >
      <HStack>
        <Flex
          alignItems="center"
          bg="gray.50"
          borderRadius="full"
          boxSize="32px"
          justifyContent="center"
        >
          <Icon as={icon} boxSize="24px" color="gray.500" />
        </Flex>
        <Text
          fontSize={styles[size].fontSize}
          fontWeight={styles[size].fontWeight}
          textDecoration="underline"
        >
          {title}
        </Text>
      </HStack>
      <Badge colorScheme={badge.color} fontSize={styles[size].badgeFontSize}>
        <HStack alignItems="center" spacing="1">
          {isBadgeLoading && status === DocumentStatus.SIGNATURES_PENDING && (
            <Spinner size="xs" speed="0.7s" />
          )}
          <Text>{badge.label}</Text>
        </HStack>
      </Badge>
    </Button>
  );
}

export type SignatureDocumentProps = Props;
